// Internationalization utilities

// Default language
export const DEFAULT_LANGUAGE = 'en-us';

// Available languages (can be expanded later)
export const AVAILABLE_LANGUAGES = {
  'en-us': 'English (US)',
  // Future languages can be added here:
  // 'es': 'Español',
  // 'fr': 'Français',
  // 'de': 'Deutsch',
} as const;

export type LanguageCode = keyof typeof AVAILABLE_LANGUAGES;

/**
 * Creates a localized path with language prefix
 * @param path - The path without language prefix (e.g., '/about')
 * @param language - The language code (defaults to DEFAULT_LANGUAGE)
 * @returns Localized path (e.g., '/en-us/about')
 */
export function createLocalizedPath(path: string, language: LanguageCode = DEFAULT_LANGUAGE): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // If path is empty (root), just return language prefix
  if (!cleanPath) {
    return `/${language}/`;
  }
  
  return `/${language}/${cleanPath}`;
}

/**
 * Extracts language code from a localized path
 * @param path - The localized path (e.g., '/en-us/about')
 * @returns Language code or null if not found
 */
export function extractLanguageFromPath(path: string): LanguageCode | null {
  const segments = path.split('/').filter(Boolean);
  const potentialLang = segments[0] as LanguageCode;
  
  if (potentialLang && potentialLang in AVAILABLE_LANGUAGES) {
    return potentialLang;
  }
  
  return null;
}

/**
 * Removes language prefix from a path
 * @param path - The localized path (e.g., '/en-us/about')
 * @returns Path without language prefix (e.g., '/about')
 */
export function removeLanguageFromPath(path: string): string {
  const segments = path.split('/').filter(Boolean);
  const potentialLang = segments[0];
  
  if (potentialLang && potentialLang in AVAILABLE_LANGUAGES) {
    // Remove the language segment
    const remainingPath = segments.slice(1).join('/');
    return remainingPath ? `/${remainingPath}` : '/';
  }
  
  return path;
}
